import { motion } from 'framer-motion';
import React from 'react';

import styles from './IconRing.module.css';

/**
 * IconRing Item Interface
 */
type IconRingItem = {
  readonly label: string;
  readonly icon: string; // Unicode emoji or icon identifier
  readonly url: string;
  readonly order: number;
};

/**
 * IconRing Props
 */
type IconRingProps = {
  readonly content: {
    readonly items?: ReadonlyArray<IconRingItem>;
  };
  readonly radius?: number;
};

/**
 * IconRing Component
 *
 * Displays icons in a circular ring layout with external links.
 * Features:
 * - Polar coordinate positioning for circular layout
 * - External links open in new tabs with security
 * - Smooth entrance animations
 * - Responsive to different icon counts (3-12 items)
 * - Pink/orange glow theme matching design system
 * @param root0
 * @param root0.content
 * @param root0.radius
 */
const IconRing: React.FC<IconRingProps> = ({ content, radius = 320 }) => {
  const items = content.items ?? [];

  // Sort items by order
  const sortedItems = [...items].sort((a, b) => a.order - b.order);

  /**
   * Calculate polar coordinates for icon positioning
   * @param index
   * @param total
   */
  const getIconPosition = (index: number, total: number) => {
    // Distribute icons evenly around the circle
    const angleStep = (2 * Math.PI) / total;
    // Start at top (-90 degrees = -π/2 radians)
    const startAngle = -Math.PI / 2;
    const angle = startAngle + index * angleStep;

    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
    };
  };

  /**
   * Handle external link click
   * @param url
   */
  const handleIconClick = (url: string) => {
    // Open in new tab with security
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Animation variants for entrance
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut' as const,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 200,
        damping: 15,
      },
    },
  };

  // Empty state
  if (items.length === 0) {
    return (
      <div className={styles.iconRing}>
        <div className={styles.empty}>
          <p>No items available</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className={styles.iconRing}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className={styles.ringContainer}>
        {sortedItems.map((item, index) => {
          const position = getIconPosition(index, sortedItems.length);

          return (
            <motion.button
              key={`${item.label}-${index}`}
              className={styles.iconButton}
              animate={{
                x: position.x,
                y: position.y,
              }}
              variants={itemVariants}
              onClick={() => handleIconClick(item.url)}
              aria-label={`${item.label} - Opens in new tab`}
            >
              <div className={styles.iconCircle}>
                <span className={styles.icon} aria-hidden="true">
                  {item.icon}
                </span>
              </div>
              <span className={styles.label}>{item.label}</span>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
};

export default IconRing;
